import type { CSSProperties } from 'react';
import toast, { ToastBar, Toaster } from 'react-hot-toast';

import cx from 'classnames';

import { GlossIcon } from 'glossDS/Icons/GlossIcon/GlossIcon';
import tokens from 'glossDS/tokens.module.css';

import s from './Toast.module.css';

const baseStyle: CSSProperties = {
  alignItems: 'center',
  background: 'var(--toast-bg)',
  borderRadius: 'var(--toast-radius)',
  boxShadow: 'var(--elevation-16)',
  color: 'var(--toast-content)',
  display: 'inline-flex',
  gap: 'var(--space-2x)',
  margin: '0',
  height: 'auto',
  padding: 'var(--space-4x)',
};

const DEFAULT_DURATION = 5_000;
const FADE_IN_DURATION = '0.3s';
const FADE_OUT_DURATION = '0.3s';

export const ToastContainer = () => (
  <Toaster
    position="bottom-center"
    reverseOrder={false}
    gutter={8}
    containerClassName={tokens.glossDS}
    containerStyle={{
      minWidth: '248px',
      zIndex: 99999,
    }}
    toastOptions={{
      success: {
        duration: DEFAULT_DURATION,
        icon: (
          <GlossIcon size="sm" icon="checkmark-circle" className={s.icon} />
        ),
        style: {
          ...baseStyle,
        },
      },
      error: {
        duration: DEFAULT_DURATION,
        icon: (
          <GlossIcon
            size="sm"
            icon="alert-circle"
            className={cx(s.icon, s.iconError)}
          />
        ),
        style: {
          ...baseStyle,
          background: 'var(--toast-bg-warning)',
          color: 'var(--toast-content-warning)',
        },
      },
      loading: {
        duration: DEFAULT_DURATION,
        icon: (
          <GlossIcon size="sm" icon="checkmark-circle" className={s.icon} />
        ),
        style: {
          ...baseStyle,
        },
      },
    }}
  >
    {(t) => (
      <ToastBar
        toast={t}
        style={{
          ...t.style,
          margin: '0',
          minWidth: 315,
          maxWidth: 480,
          animation: t.visible
            ? // 'fade-in' and 'fade-out' are @keyframes defined in the css module
              `${s['fade-in']} ${FADE_IN_DURATION} ease-in-out forwards`
            : `${s['fade-out']} ${FADE_OUT_DURATION} ease-in-out forwards`,
        }}
      >
        {({ icon, message }) => (
          <div className={s.root}>
            <div className={s.contents}>
              {icon}
              {message}
            </div>
            <button
              aria-label={'Dismiss notification'}
              className={s.dismissButton}
              onClick={() => toast.dismiss(t.id)}
            >
              <GlossIcon className={s.icon} icon="close-outline" size="sm" />
            </button>
          </div>
        )}
      </ToastBar>
    )}
  </Toaster>
);

export const Toast = toast;
